import { Client } from "@/Common/Api/Client";
import { ClientResponse } from "@/Common/Api/ClientResponse";
import { ErrorMessage } from "@/Common/Api/Dto/ErrorMessage";
import { Employee } from "@/Common/Api/Employee/Dto/Employee";
import { LabCriteriasDTO } from "@/Common/Api/Labs/Dto/Responses/LabCriteriasDTO";
import { BaseReactComponent } from "@/Common/Components/BaseReactComponent";
import { DocumentProgressBarPopup } from "@/Common/Components/ProgressBar/DocumentProgressBarPopup";
import { PersonSearchAutocompleteCustom } from "@/Common/Components/Search/PersonSearchAutocompleteCustom";
import { DateTime } from "@/Common/Helpers/DateTime";
import { SiteHelper } from "@/Common/Helpers/SiteHelper";
import { FileSelectionContainerType } from "@/Common/Models/FileSelection/FileSelectionContainerType";
import { PatientRefIntervenorWithDetailsModel } from "@/Common/Models/Patient/PatientRefIntervenorWithDetailsModel";
import { Person } from "@/Common/Models/Person/Person";
import { SortDirection } from "@/Common/Models/UI/Enums";
import { PersonDropdownItem } from "@/Common/Models/UI/Inputs/PersonDropdownItem";
import * as ServerOperations from "@/Common/Operations/operations.server";
import { FaxAndSignatureClinicianSelectionState } from "@/ExportableFile/Components/FaxAndSignatureClinicianSelection";
import { ExportableFileDispatcher } from "@/ExportableFile/Helpers/ExportableFileDispatcher";
import { ExportableFileHelper } from "@/ExportableFile/Helpers/ExportableFileHelper";
import { ExportableFile } from "@/ExportableFile/Models/ExportableFile";
import { ExportableFileResponse } from "@/ExportableFile/Models/ExportableFileResponse";
import { ExportableFileModel } from "@/ExportableFile/Models/ExportableFileTableItem";
import { FileTypeMedicalSummary } from "@/ExportableFile/Models/FileTypes/FileTypeMedicalSummary";
import { FileTypeMedicationSummary } from "@/ExportableFile/Models/FileTypes/FileTypeMedicationSummary";
import { FileSelectionComponent } from "./FileSelectionComponent";
import { FileSelectionSettingComponentState } from "./FileSelectionSettingComponent";

import img_loading from "@/Globals/_images/loading.gif";

interface IFileSelectionContainerProps {
    title: string;
    type: FileSelectionContainerType;

    patient: Person;
    licencedIntervenorOnFile?: PatientRefIntervenorWithDetailsModel[];

    maxFilesAttachment?: number;

    onClose: () => void;
    onBack?: () => void;
    loadFileList?: (patient: Person, setting?: FileSelectionSettingComponentState) => Promise<ClientResponse<{ PatientPrintableFiles: ExportableFileResponse[] }>>;
    addSummaries?: (fileList: Immutable.List<ExportableFileModel>, patientContactId: string) => Immutable.List<ExportableFileModel>;
    onPrintOrAttachClicked?: (selected: ExportableFile[]) => void;
    encounterContexts?: string[];

    popupStyle?: React.CSSProperties;
    autoManageOverlay?: boolean;
    selectedFiles?: ExportableFile[];
}

class FileSelectionContainerState {
    public fileList: ExportableFileModel[];
    public selectedFile: ExportableFileModel;
    public checkedFiles: ExportableFileModel[];
    public currentPatient: Person;
    public isLoading: boolean;

    // Fax related state
    public showFaxAndSignPopup: boolean;
    public previewMergedPdfPath: string;
    public mergedFileName: string;
    public isFaxValid: boolean;
    public clinicianInfoState: FaxAndSignatureClinicianSelectionState;

    public numberOfDocumentsGenerated: number;
    public numberOfDocumentsToGenerate: number;
}

class FileSelectionContainer extends BaseReactComponent<IFileSelectionContainerProps, FileSelectionContainerState> {
    private readonly fileSelectionKey = "FileSelectionContainer";

    constructor(props: IFileSelectionContainerProps) {
        super(props);

        const initialState = new FileSelectionContainerState();
        initialState.fileList = [];
        initialState.isLoading = true;
        initialState.selectedFile = null;
        initialState.checkedFiles = [];
        initialState.currentPatient = this.props.patient;
        this.state = initialState;
    }

    public componentDidMount(): void {
        if (this.props.autoManageOverlay)
            overlayStore.showOverlay(this.fileSelectionKey);

        window.setTimeout(() => {
            $(window).trigger("resize");
        }, 200);

        this.loadFileList(this.props.patient);
    }

    public componentWillUnmount(): void {
        if (this.props.autoManageOverlay)
            overlayStore.hideOverlay(this.fileSelectionKey);
    }

    public render(): JSX.Element {
        if (!this.state.currentPatient)
            return this.renderPreSelectPatientRequierement();

        return (
            <div
                id={`file_selection_${this.state.currentPatient.ContactId}`}
                className="filePreview-popup popup-attachment-main popup-with-grid"
                style={this.props.popupStyle}
            >
                <div className="dam-header p-header">
                    <div className="flex-container">
                        <div>
                            <h2>{this.props.title}</h2>
                        </div>

                        <div className="flex-container popup-attachment-header">
                            <div className="popup-attachment-header-patientInfo">
                                {this.state.currentPatient.GetNameWithNam()}
                            </div>
                            <div className="flex-container popup-attachment-header-right">
                                <div className="popup-attachment-header-right-CloseWrapper">
                                    <div className="pointer popup-attachment-header-right-btnClose" onClick={() => this.props.onClose()}>X</div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="dam-main">
                    <FileSelectionComponent
                        patient={this.state.currentPatient}
                        fileList={this.state.fileList}
                        selectedFile={this.state.selectedFile}
                        checkedFiles={this.state.checkedFiles}

                        onFileSelected={selected => this.handleOnFileSelected(selected)}
                        onRefreshClicked={setting => this.loadFileList(this.state.currentPatient, setting)}
                        onFilePreviewLoaded={() => this.mutateState(s => s.isLoading = false)}
                        onAllItemCheckboxChanged={(isChecked, itemIds) => this.handleOnAllItemChecked(isChecked, itemIds)}
                        onItemCheckboxChanged={(model, isChecked) => this.handleOnItemChecked(model, isChecked)}
                        onSortDone={(item, newIndex) => this.handleOnSortDone(item, newIndex)}
                    />
                </div>

                {this.state.numberOfDocumentsToGenerate > 0 && (
                    <DocumentProgressBarPopup
                        maxValue={this.state.numberOfDocumentsToGenerate}
                        currentValue={this.state.numberOfDocumentsGenerated}
                    />
                )}

                <div className="dam-footer">
                    <div className="row">
                        <div className="col-lg-9 col-md-9 col-sm-9">
                            {this.state.isLoading && <img src={img_loading} alt="loading" />}
                        </div>
                        <div className="col-lg-3 col-md-3 text-right">
                            <div className="flex-container justifyContent-end">
                                {this.renderFooterControls()}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    public renderPreSelectPatientRequierement(): JSX.Element {
        return (
            <div id="patientSearchFileList" className="filePreview-popup popup-attachment-preSelectPatient-container">
                <div className="dam-header p-header draggable">
                    <div className="flex-container" style={{ justifyContent: "space-between" }}>
                        <div>
                            <h2>{cacheStore.GetString("PatientLabel")}</h2>
                        </div>
                        <div onClick={() => this.props.onClose()}>
                            <a className="close">x</a>
                        </div>
                    </div>
                    <div className="dam-main">
                        <PersonSearchAutocompleteCustom
                            useBlurEvent={true}
                            focusOnLoad={this.state.currentPatient == null}
                            disabled={false}
                            componentId="add_attachmentpopup_patients"
                            selectedItemList={this.state.currentPatient == null ? [].toImmutable() : [this.state.currentPatient].toImmutable()}
                            onSelectionCallback={(PDI: PersonDropdownItem) => {
                                this.mutateState(
                                    s => { s.currentPatient = PDI.Item; },
                                    () => { this.loadFileList(this.state.currentPatient); }
                                );
                            }}
                            onClearCallback={() => {
                                this.mutateState(s => {
                                    s.currentPatient = null;
                                    s.fileList = [];
                                });
                            }}
                            closeDropDownAfterSelection={true}
                            defaultTextValue={this.state.currentPatient.NameWithPrefix}
                        />
                    </div>
                </div>
            </div>
        );
    }

    private renderFooterControls(): JSX.Element {
        let selectedFiles = this.getSelectedFiles();
        let faxAccounts = cacheStore.faxAccounts;
        let buttons: JSX.Element[] = [];

        switch (this.props.type) {
            case FileSelectionContainerType.Printing:
                if (faxAccounts && faxAccounts.length > 0) {
                    buttons.push(
                        <button
                            key="fax"
                            className="btn btn-primary popup-attachment-footer-btn"
                            disabled={selectedFiles.length === 0}
                            onClick={() => ExportableFileDispatcher.openMultipleFilesSignFaxPrintPopup({
                                patientContactId: this.state.currentPatient.ContactId,
                                files: selectedFiles?.map(p => p.Model),
                                mutateState: mutation => this.mutateState(s => mutation(s)),
                                onDoneMerging: () => this.props.onClose(),
                                defaultContext: this.props.licencedIntervenorOnFile,
                                useLabsSearch: cacheStore.ClinicSettings.EnableMedfarDictionaryLabSearchInsteadOfDoctorSearchForBcClients
                            })}
                        >
                            {cacheStore.GetString("FaxVerbLabel")}
                        </button>
                    );
                }
                buttons.push(
                    <button
                        key="print"
                        className="btn btn-primary popup-attachment-footer-btn"
                        disabled={selectedFiles.length === 0}
                        onClick={() => this.handleOnPrintOrAttach()}
                    >
                        {cacheStore.GetString("PrintLabel")}
                    </button>
                );
                break;
            case FileSelectionContainerType.Attachment:
                buttons.push(
                    <button
                        key="attach"
                        className="btn btn-primary popup-attachment-footer-btn"
                        disabled={selectedFiles.length === 0}
                        onClick={() => this.handleOnPrintOrAttach()}
                        data-cy="fileSelectionContainer-attach"
                    >
                        {cacheStore.GetString("AttachLabel")}
                    </button>
                );
                break;
            case FileSelectionContainerType.ImportFiles:
                buttons.push(
                    <button
                        key="back"
                        className="btn btn-default popup-attachment-footer-btn"
                        onClick={() => this.props?.onBack()}
                    >
                        {cacheStore.GetString("BackLabel")}
                    </button>
                );
                break;
        }
        return (
            <div>
                <button className="btn btn-default" onClick={() => this.props.onClose()}>{cacheStore.GetString("CloseLabel")}</button>
                {buttons}
            </div>
        );
    }

    private handleOnFileSelected(file: ExportableFileModel): void {
        this.mutateState(s => {
            s.isLoading = true;
            s.selectedFile = file;
            s.selectedFile.isLoaded = true;
        });
    }

    private handleOnAllItemChecked(isCheck: boolean, itemIds?: number[]): void {
        this.mutateState(s => {
            if (itemIds && itemIds.any())
                s.fileList.filter(x => itemIds.contains(x.itemId)).forEach(x => this.handleOnCheckFile(x, isCheck, s.checkedFiles));
            else
                s.fileList.forEach(x => this.handleOnCheckFile(x, isCheck, s.checkedFiles));
        });
    }

    private handleOnItemChecked(file: ExportableFileModel, isChecked: boolean): void {
        this.mutateState(s => {
            let fileFromFileList = file;

            for (const x of s.fileList) {
                if (x.itemId === file.itemId) {
                    fileFromFileList = x;
                    break;
                }
            }

            this.handleOnCheckFile(fileFromFileList, isChecked, s.checkedFiles);
        });
    }

    private handleOnPrintOrAttach(): void {
        this.mutateState(
            s => s.isLoading = true,
            () => {
                if (this.props.maxFilesAttachment && this.props.maxFilesAttachment < this.state.checkedFiles.length) {
                    let errorMsg = new ErrorMessage();
                    errorMsg.Message = `${cacheStore.GetString("MaxNumberAttachFilesLabel")} ${this.props.maxFilesAttachment}`;
                    this.handleError([errorMsg]);
                    return;
                }

                this.props.onPrintOrAttachClicked && this.props.onPrintOrAttachClicked(this.getFilesToBePrinted());
            }
        );
    }

    private async loadFileList(patient: Person, setting?: FileSelectionSettingComponentState): Promise<void> {
        if (!patient)
            return;

        if (!setting)
            setting = FileSelectionSettingComponentState.createDefault();

        let clientResponse: ClientResponse<{ PatientPrintableFiles: ExportableFileResponse[] }>;

        if (this.props.loadFileList) {
            clientResponse = await this.props.loadFileList(patient, setting);
        }
        else {
            let request = {
                PatientContactId: patient.ContactId,
                DateFrom: setting.dateFrom,
                DateTo: setting.dateTo,
                Categories: setting.categories.filter(c => c.Checked == true).map(c => c.Id).toArray(),
                LabCriterias: this.initPatientLabCriterias(),
                EncounterContexts: null
            };

            if (cacheStore.ClinicSettings.EnableDirectPatientProfileEdits) {
                request.EncounterContexts = this.props.encounterContexts;
            }

            clientResponse = await Client.postAsync(
                ServerOperations.Server.Myle_GetPatientPrintableFiles,
                request
            );
        }

        if (clientResponse.isSuccess) {
            this.handleLoadFileListSuccess(clientResponse.data, setting);
        }
        else {
            this.handleError(clientResponse.errors);
        }
    }

    private handleLoadFileListSuccess(response: { PatientPrintableFiles: ExportableFileResponse[] }, setting?: FileSelectionSettingComponentState): void {
        this.mutateState(s => {
            if (!s.currentPatient) {
                s.isLoading = false;
                s.fileList = [];
                return;
            }

            s.isLoading = false;
            s.fileList = (response.PatientPrintableFiles ?? [])
                .map(x => ExportableFileHelper.fromExportableFileResponse(x).toExportableFileModel())
                .filter(x => x);

            // Add Summaries
            let checkedCats = setting && setting.categories.filter(c => c.Checked).toArray() || [];
            if (checkedCats.length == 0 || checkedCats.some(c => c.Id == "CategorySummaryId")) {
                if (!setting || cacheStore.Helper.isDateIncluded(setting.dateFrom, setting.dateTo, DateTime.nowDate())) {
                    s.fileList = this.addSummaries(s.fileList.toImmutable(), this.props.patient.ContactId).toArray();
                }
            }

            if (!isArrayNullOrUndefinedOrEmpty(this.props.selectedFiles))
                s.fileList.mutate(x => {
                    if (this.props.selectedFiles.some(file => file.isEquivalent(x.Model))) {
                        this.handleOnCheckFile(x, true, s.checkedFiles);
                        x.isDisabled = true;
                    }
                });

            if (!isArrayNullOrUndefinedOrEmpty(s.checkedFiles)) {
                s.fileList.mutate(file => {
                    // ids change after reload, so we need to replace old files in checkedFiles list too
                    let similarFileInCheckedFiles = s.checkedFiles.find(p =>
                        p.Model.isEquivalent(file.Model)
                    );

                    if (similarFileInCheckedFiles) {
                        file.isChecked = true;

                        const indexOfItemToReplace = s.checkedFiles.indexOf(similarFileInCheckedFiles);
                        s.checkedFiles.splice(indexOfItemToReplace, 1, file);
                    }
                });
            }
        });
    }

    private addSummaries(fileList: Immutable.List<ExportableFileModel>, patientContactId: string): Immutable.List<ExportableFileModel> {
        if (this.props.addSummaries) {
            return this.props.addSummaries(fileList, patientContactId);
        }

        const fileTypeMedicalSummary = new FileTypeMedicalSummary(patientContactId);
        const fileTypeMedicationSummary = new FileTypeMedicationSummary(patientContactId);

        return fileList
            .push(fileTypeMedicalSummary.toExportableFileModel())
            .push(fileTypeMedicationSummary.toExportableFileModel());
    }

    private handleError(errors: ErrorMessage[]): void {
        if (!errors || errors.length === 0)
            return;

        this.mutateState(
            s => s.isLoading = false,
            () => sessionStore.showPopup(errors)
        );
    }

    private getSelectedFiles(): ExportableFileModel[] {
        if (this.state.checkedFiles.length === 0 && this.state.selectedFile != null)
            return [this.state.selectedFile];
        else
            return this.state.checkedFiles;
    }

    private initPatientLabCriterias(): LabCriteriasDTO {
        let criterias = new LabCriteriasDTO();
        criterias.Text = "";
        criterias.Page = 1;
        criterias.PageSize = 500; // its a patient File... just setting a limit to prevent loading a bugged or test patient with 500++ labs
        criterias.From = null;
        criterias.To = null;
        criterias.Abnormality = null;

        criterias.ToConsentDate = new Date(2050, 1, 1, 0, 0, 0, 0);
        criterias.sortdirection = SortDirection.DESC;

        criterias.LicenseNumbers = cacheStore.EmployeeCacheWithInactive
            .map(emp => Employee.getLicenseDto(emp))
            .filter(p => p?.LicenseNumber);

        // Yourself
        criterias.UserLicenseNumber = sessionStore.getCurrentEmployeeLicenseDto();
        criterias.ClinicianType = [4, 5, 6];

        criterias.IsSpecificSearch = false;
        criterias.SpecificLabsIDs = null;

        criterias.IsPatientSearch = true;
        criterias.PatientContactId = this.state.currentPatient.ContactId;

        criterias.IsNew = null;
        criterias.IsSubmitted = null;
        criterias.IsClassified = null;

        criterias.IgnoreContextFilterForCurrentUser = false;
        criterias.ContextFilter = SiteHelper.GetContexts();

        criterias.PortalSyncStatuses = null;
        criterias.PortalSyncUserContactIds = null;

        return criterias;
    }

    private handleOnSortDone(item: ExportableFileModel, newIndex: number): void {
        this.mutateState(s => {
            const fromIndex = s.checkedFiles.findIndex(p => item.itemId === p.itemId);
            const element = s.checkedFiles.splice(fromIndex, 1)[0];
            s.checkedFiles.splice(newIndex, 0, element);
        });
    }

    private handleOnCheckFile(file: ExportableFileModel, isChecked: boolean, checkedFiles: ExportableFileModel[]): void {
        file.isChecked = isChecked;

        const checkedFile = checkedFiles?.find(p => p.itemId === file.itemId);

        // Remove file from checked list
        if (checkedFile && !isChecked)
            checkedFiles.splice(checkedFiles.indexOf(checkedFile), 1);

        // Add file to checked list
        if (!checkedFile && isChecked)
            checkedFiles.push(file);
    }

    private getFilesToBePrinted(): ExportableFile[] {
        if (!isArrayNullOrUndefinedOrEmpty(this.state.checkedFiles))
            return this.state.checkedFiles.map(x => x.Model);
        else if (this.state.selectedFile)
            return [this.state.selectedFile.Model];
        else
            return [];
    }
}

export { FileSelectionContainer };
